import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ListChecks, RotateCw } from 'lucide-react';
import AppContext from 'contexts/AppContext';
import { hasRole, ROLES } from 'utils/roleUtils';
import { apiErrorMessage } from 'utils/errorMessage';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { EmptyState } from 'components/EmptyState';
import { useToast } from 'components/ui/toast';
import AccessDenied from 'pages/AdminAudit/AccessDenied';
import { journalLink, RealignmentConflictError } from 'api/sectorAlignmentApi';
import type { DeskAnalysis, DeskSource, LastRun, SectorAnalysis, SectorDesk } from 'api/sectorAlignmentApi';
import { isJobRunning, useDeskInfo, useSectorAlignmentDesks, useSectorAnalysis, useSectorRealignmentJob, useStartRealignment } from 'hooks/useSectorAlignment';
import ReferentialPageHeader from '../components/ReferentialPageHeader';
import ReferentialBoundary from '../components/ReferentialBoundary';
import AlignmentTabs from './AlignmentTabs';
import Banner from './Banner';
import DeskCard from './DeskCard';
import DeskSelect from './DeskSelect';
import MobileDeskList from './MobileDeskList';
import RealignDialog from './RealignDialog';
import { cleanSelection, clearSelection, readSelection, withLocked, writeSelection } from './deskSelection';
import { allAligned, cardDesks, deskView, initialState, reducer, selectionChanged, tabDesks } from './pageState';
import type { Scope } from './pageState';
import { dayOf, isStale, timeOf } from './staleness';

/** A failed request (network, 500) reads as « ne répond pas » on each desk it covered; the card picks the copy from its source. */
const errorDesk = (sd: SectorDesk, source: DeskSource | null): DeskAnalysis => ({
  sd, source: source ?? 'jira-assets', status: 'error', misaligned: 0, closed: 0, stores: [], unreadable: 0, jira: [], ecm: [], impact: [], analyzedAt: new Date().toISOString(),
});

const oldest = (dates: string[]): string => dates.reduce((min, d) => (d < min ? d : min), dates[0] ?? '');

const SectorAlignment = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { currentUser } = useContext(AppContext);
  const userId = currentUser?.accountId; // App.js gives no `id`; accountId always gates the login
  const isSuperAdmin = hasRole(currentUser?.role, ROLES.SUPERADMIN);
  const { data: deskList, isError: desksFailed, isSuccess } = useSectorAlignmentDesks({ enabled: isSuperAdmin });
  const serviceDesks = deskList ?? [];
  const infoOf = useDeskInfo();
  const { mutateAsync: fetchAnalysis } = useSectorAnalysis();
  const startRealignment = useStartRealignment();
  const [state, dispatch] = useReducer(reducer, Date.now(), initialState);
  const latest = useRef(state);
  useEffect(() => {
    latest.current = state;
  }, [state]);

  // AC19: restore the remembered selection once the desk list is known.
  useEffect(() => {
    if (deskList) dispatch({ type: 'init', ...cleanSelection(readSelection(userId), deskList) });
  }, [deskList, userId]);

  const analyze = useCallback(
    async (sds: SectorDesk[], scope: Scope): Promise<SectorAnalysis | null> => {
      if (!sds.length) return null;
      dispatch({ type: 'analyze', sds, scope }); // AC12: busy before the request leaves
      try {
        const result = await fetchAnalysis(sds);
        const desks = Array.isArray(result?.desks) ? result.desks : [];
        dispatch({ type: 'analyzed', sds, scope, desks: sds.map((s) => desks.find((d) => d.sd === s) ?? errorDesk(s, infoOf(s).source)), lastRun: result?.lastRun ?? null });
        return result;
      } catch (error) {
        console.error('Sector alignment analysis failed:', error);
        dispatch({ type: 'analyzed', sds, scope, desks: sds.map((s) => errorDesk(s, infoOf(s).source)) });
        return null;
      }
    },
    [fetchAnalysis, infoOf]
  );

  // 7e: the job left the running states; re-read its desk (the whole selection when nothing was analysed yet).
  const rerunDesk = useCallback(
    async (sd: SectorDesk) => {
      const { selected, desks, lastRun: previous } = latest.current;
      if (!selected?.includes(sd)) return; // AC20: another admin's job on a desk not shown here
      dispatch({ type: 'finished', sd });
      const result = await (desks.length ? analyze([sd], 'desk') : analyze(selected, 'selection'));
      const run = result?.lastRun;
      const desk = result?.desks?.find((d) => d.sd === sd);
      const previousAt = previous?.at;
      if (run?.sd === sd && (!previousAt || run.at > previousAt) && run.outcome === 'done' && desk?.status === 'ok' && desk.misaligned === 0) {
        toast.success(t('{{count}} tickets realigned', { count: run.applied }));
      }
    },
    [analyze, toast, t]
  );

  const { job } = useSectorRealignmentJob(rerunDesk, { enabled: isSuperAdmin });
  const running = isJobRunning(job) ? job : undefined;

  useEffect(() => {
    const timer = setInterval(() => dispatch({ type: 'tick', now: Date.now() }), 60_000);
    return () => clearInterval(timer);
  }, []);

  if (!isSuperAdmin) {
    return <AccessDenied message={t('This page is reserved for super admins.')} />;
  }

  const { desks, lastRun, analyzing, now } = state;
  const selected = state.selected ?? [];
  const busy = analyzing.length > 0;
  const results = selected.flatMap((sd) => desks.find((d) => d.sd === sd) ?? []);
  const okDesks = results.filter((d) => d.status === 'ok');
  const lastRunOf = (sd: SectorDesk): LastRun => (lastRun?.sd === sd ? lastRun : null);
  const isAnalyzing = (sd: SectorDesk) => analyzing.some((e) => e.sd === sd);
  const staleDesks = okDesks.filter((d) => !isAnalyzing(d.sd) && isStale(d.analyzedAt, lastRunOf(d.sd)?.at, now));
  const unreadable = okDesks.reduce((sum, d) => sum + (Number(d.unreadable) || 0), 0);
  const cards = cardDesks(state, running?.sd);
  const lockedSd = running?.sd != null && selected.includes(running.sd) ? running.sd : null;
  const realignDesk = desks.find((d) => d.sd === state.realignDesk) ?? null;

  const changeSelection = (sds: SectorDesk[]) => {
    dispatch({ type: 'select', sds });
    writeSelection(userId, sds);
  };
  const resetSelection = () => {
    dispatch({ type: 'select', sds: withLocked(serviceDesks.filter((d) => d.applicable).map((d) => d.sd), lockedSd) });
    clearSelection(userId);
  };
  const analyseSelection = () => analyze(selected, 'selection');

  const confirmRealign = () => {
    if (!realignDesk) return;
    startRealignment.mutate(realignDesk.sd, {
      onSuccess: () => dispatch({ type: 'openRealign', sd: null }),
      onError: (error) => {
        if (!(error instanceof RealignmentConflictError)) {
          dispatch({ type: 'realignError', message: apiErrorMessage(t, error) });
          return;
        }
        dispatch({ type: 'openRealign', sd: null });
        toast.error(
          error.startedBy
            ? t('A realignment is already running (started by {{name}}). Nothing was started.', { name: error.startedBy })
            : t('A realignment is already running. Nothing was started.')
        );
      },
    });
  };

  const renderCard = (sd: SectorDesk) => {
    const view = deskView(state, sd);
    return (
      <ReferentialBoundary key={sd} message={t('This service desk could not be displayed.')}>
        <DeskCard
          {...infoOf(sd)}
          desk={desks.find((d) => d.sd === sd)}
          analyzing={view === 'skeleton'}
          refreshing={view === 'refreshing'}
          progress={running?.sd === sd ? running.progress ?? { done: 0, total: 0 } : null}
          busyElsewhere={Boolean(running)}
          lastRun={lastRunOf(sd)}
          justFinished={state.justFinished === sd}
          now={now}
          onRetry={() => analyze([sd], 'desk')}
          onRealign={() => dispatch({ type: 'openRealign', sd })}
        />
      </ReferentialBoundary>
    );
  };

  return (
    <div className="container mx-auto space-y-5 p-4 md:p-6">
      {/* §1.8: no header icon below md. */}
      <div className="flex flex-col gap-1 max-md:[&>div>div>span:first-child]:hidden">
        <ReferentialPageHeader
          icon={RotateCw}
          title={t('Sector alignment')}
          subtitle={t("Find the tickets left on their store's former sector and realign them.")}
        />
        {lastRun && (
          <p className="hidden pl-[52px] text-[12.5px] text-muted-foreground md:block">
            {t('Last realignment on {{date}} at {{time}} by {{name}} · {{desk}} · {{count}} tickets ·', {
              date: dayOf(lastRun.at),
              time: timeOf(lastRun.at),
              name: lastRun.actor?.name || '—',
              desk: infoOf(lastRun.sd).name,
              count: lastRun.applied,
            })}{' '}
            <Link to={journalLink(lastRun.realignmentId)} className="text-muted-foreground underline underline-offset-[3px]">
              {t('View in the journal')}
            </Link>
          </p>
        )}
      </div>

      {desksFailed ? (
        <p role="alert" className="text-[13.5px] text-muted-foreground">{t('The service desks could not be loaded.')}</p>
      ) : isSuccess && serviceDesks.length === 0 ? (
        <p className="text-[13.5px] text-muted-foreground">{t('No service desk is configured.')}</p>
      ) : state.selected === null ? (
        <Skeleton className="h-10 w-full max-w-[760px] dark:bg-zinc-800" />
      ) : (
        <ReferentialBoundary message={t('The service desk selector could not be displayed.')}>
          <DeskSelect
            desks={serviceDesks}
            selected={selected}
            analysed={desks.map((d) => d.sd)}
            lockedSd={lockedSd}
            restored={state.restored}
            busy={busy}
            onChange={changeSelection}
            onReset={resetSelection}
            onAnalyse={analyseSelection}
          />
        </ReferentialBoundary>
      )}

      {selected.length > 0 && selectionChanged(state) && <Banner tone="amber">{t('The selection has changed: relaunch the analysis.')}</Banner>}
      {running?.startedBy && running.startedBy !== currentUser?.username && (
        <Banner tone="sky">
          {t('Realignment in progress, started by {{name}} at {{time}}', {
            name: running.startedBy,
            time: timeOf(running.startedAt),
          })}
        </Banner>
      )}
      {staleDesks.length > 0 && (
        <Banner
          tone="amber"
          action={
            <Button variant="outline" size="sm" onClick={analyseSelection} disabled={busy}>
              <RotateCw aria-hidden="true" />
              {t('Relaunch the analysis')}
            </Button>
          }
        >
          {t('This result dates from {{time}}. The data may have changed.', { time: timeOf(oldest(staleDesks.map((d) => d.analyzedAt))) })}
        </Banner>
      )}

      {results.length > 0 && (
        <p className="text-[13px] text-muted-foreground">
          {t('Analyzed at {{time}} · Nothing is saved yet.', { time: timeOf(oldest(results.map((d) => d.analyzedAt))) })}
        </p>
      )}
      {unreadable > 0 && (
        <Banner tone="amber">{t('{{count}} stores could not be read in Jira: incomplete result.', { count: unreadable })}</Banner>
      )}

      {cards.length > 0 && (
        <>
          <div className="hidden grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-4 md:grid">{cards.map(renderCard)}</div>
          <ReferentialBoundary message={t('The service desks could not be displayed.')}>
            <MobileDeskList
              desks={cards.map((sd) => ({ sd, ...infoOf(sd), result: desks.find((d) => d.sd === sd), view: deskView(state, sd) }))}
              renderCard={renderCard}
            />
          </ReferentialBoundary>
        </>
      )}

      {busy && results.length === 0 && (
        <p className="text-[13px] text-muted-foreground">{t('The analysis is not kept: if you leave the page, it will be lost.')}</p>
      )}
      {allAligned(state) && <p className="text-[13.5px] text-muted-foreground">{t('No misaligned ticket on the selected desks.')}</p>}

      {state.selected !== null && serviceDesks.length > 0 && selected.length === 0 && (
        <EmptyState icon={ListChecks} title={t('No service desk selected')} hint={t('Choose at least one desk above to run the analysis.')} />
      )}
      {selected.length > 0 && !busy && cards.length === 0 && (
        <div className="flex flex-col items-center gap-3 px-5 pb-[130px] pt-[110px] text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-100 dark:bg-zinc-800">
            <RotateCw className="h-5 w-5" aria-hidden="true" />
          </span>
          <p className="max-w-[500px] text-sm leading-relaxed text-muted-foreground">
            {t("Compares each ticket's sector with its store's current sector in Jira Assets or Globe Data.")}
            <br />
            {t('Nothing is changed.')}
          </p>
        </div>
      )}

      {/* §1.8: below md the unfolded card carries the details (AC23). */}
      <div className="hidden md:block">
        <ReferentialBoundary message={t('The analysis tables could not be displayed.')}>
          <AlignmentTabs desks={tabDesks(state)} />
        </ReferentialBoundary>
      </div>

      <ReferentialBoundary message={t('The realignment dialog could not be displayed.')}>
        <RealignDialog
          desk={realignDesk}
          deskName={realignDesk ? infoOf(realignDesk.sd).name : ''}
          pending={startRealignment.isPending}
          error={state.realignError}
          onCancel={() => dispatch({ type: 'openRealign', sd: null })}
          onConfirm={confirmRealign}
        />
      </ReferentialBoundary>
    </div>
  );
};

export default SectorAlignment;
